import {
  DATE,
  SESSION_TYPES,
  DirectionTypes,
  DispositionTypes,
  IWebexCallHistoryResponse
} from '../../../adapter-interfaces/src';

const now = Date.now();

const BASE_CONTACTS = [
  { name: 'Alice Adams', phone: '+14085550101' },
  { name: 'Ben Brown', phone: '+14085550102' },
  { name: 'Chris Carter', phone: '+14085550103' },
  { name: 'Dana Dawson', phone: '+14085550104' },
  { name: 'Eli Edwards', phone: '+14085550105' },
  { name: 'Faith Flynn', phone: '+14085550106' },
  { name: 'Gina Gomez', phone: '+14085550107' },
  { name: 'Hank Hughes', phone: '+14085550108' },
  { name: 'Ivy Isaacs', phone: '+14085550109' },
  { name: 'Jude Johnson', phone: '+14085550110' },
];

function pickContact(index: number) {
  return BASE_CONTACTS[index % BASE_CONTACTS.length];
}

type MockSessionOptions = {
  index: number;
  variant: number;
  isDuplicate?: boolean;
  isRead?: boolean;
};

function buildMockSession({ index, variant, isDuplicate = false, isRead = false }: MockSessionOptions) {
  const contact = pickContact(index);
  const sessionId = `mock-session-${index}`;
  const start = new Date(now - (index * 45 + variant * 2) * 60 * 1000);
  const end = new Date(start.getTime() + 120 * 1000);
  const direction = index % 2 === 0 ? DirectionTypes.INCOMING : DirectionTypes.OUTGOING;
  const disposition = index % 3 === 0 ? DispositionTypes.MISSED : DispositionTypes.INITIATED;
  const idSuffix = isDuplicate ? `dup-${variant}` : `orig-${variant}`;

  // ~25% of non-missed sessions have AI summaries
  // index % 3 === 0 are MISSED, so pick indices where index % 4 === 1 (1,5,9,13...)
  // but skip MISSED ones. Simpler: just check non-missed + every 3rd of those.
  const hasAISummary = disposition !== DispositionTypes.MISSED && (index % 2 === 1);
  const containerIdSuffix =
    index === 5 ? '-pending' :   // Session 5: "generating..." state
    index === 7 ? '-error' :     // Session 7: error state
    '';                           // All others: happy path

  return {
    id: `${sessionId}-${idSuffix}`,
    durationSecs: 180,
    self: {
      id: 'self-user',
      name: 'Self User',
      incomingCallProtocols: [],
      callbackInfo: {
        // Use plain phone number (no 'tel:' scheme) to avoid URL parsing issues in CrossLaunchService
        callbackAddress: '+14085550000',
        callbackType: 'PHONE',
      },
    },
    url: `https://example.com/call/${sessionId}`,
    sessionId,
    sessionType: SESSION_TYPES.WEBEXCALLING,
    startTime: start.toISOString(),
    endTime: end.toISOString(),
    direction,
    disposition,
    other: {
      id: `contact-${index}`,
      name: contact.name,
      phoneNumber: contact.phone,
      isPrivate: false,
      callbackAddress: `sip:${contact.phone}@example.com`,
    },
    durationSeconds: 180,
    joinedDurationSeconds: 0,
    participantCount: 2,
    links: {
      locusUrl: `https://example.com/locus/${sessionId}`,
      callbackAddress: `sip:${contact.phone}@example.com`,
    },
    isDeleted: false,
    isPMR: false,
    correlationIds: [],
    isRead,
    ...(hasAISummary
      ? {
          extensionPayload: {
            callingContainerIds: [`mock-container-${sessionId}${containerIdSuffix}`],
          },
        }
      : {}),
  };
}

function buildMockSessions(): ReturnType<typeof buildMockSession>[] {
  const sessions = [] as ReturnType<typeof buildMockSession>[];
  const duplicateTargetCount = 100;

  for (let i = 0; i < 120; i++) {
    sessions.push(buildMockSession({ index: i, variant: 0, isDuplicate: false, isRead: i % 4 === 0 }));

    if (i < 2) {
      for (let duplicateIndex = 1; duplicateIndex <= duplicateTargetCount; duplicateIndex++) {
        sessions.push(
          buildMockSession({
            index: i,
            variant: duplicateIndex,
            isDuplicate: true,
            isRead: duplicateIndex % 3 === 0,
          })
        );
      }
    }
  }

  return sessions;
}

export const MOCK_CALL_HISTORY_SESSIONS = buildMockSessions();
const PARTIAL_SESSION_IDS = new Set(['mock-session-0', 'mock-session-1']);
const MOCK_PARTIAL_SESSIONS = MOCK_CALL_HISTORY_SESSIONS.filter((session) =>
  PARTIAL_SESSION_IDS.has(session.sessionId)
).slice(0, 200);

const PARTIAL_FETCH_DELAY_MS = 2000;
const FULL_FETCH_DELAY_MS = 10000;
const IS_TEST_ENV = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';

export async function getMockCallHistoryResponse({ fullFetch = false }: { fullFetch?: boolean } = {}): Promise<IWebexCallHistoryResponse> {
  const delayMs = IS_TEST_ENV ? 0 : fullFetch ? FULL_FETCH_DELAY_MS : PARTIAL_FETCH_DELAY_MS;
  if (delayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  const userSessions = fullFetch ? MOCK_CALL_HISTORY_SESSIONS : MOCK_PARTIAL_SESSIONS;

  return {
    statusCode: 200,
    message: `Mocked ${userSessions.length} call history records for dev mode (${fullFetch ? 'full' : 'partial'} fetch)`,
    data: {
      userSessions,
    },
  };
}

export function isMockCallHistoryDataFresh(date: Date): boolean {
  const thirtyDaysMs = DATE.MONTH * 24 * 60 * 60 * 1000;
  return now - date.getTime() <= thirtyDaysMs;
}
